import { CARD_COMPARISON_LIMITS, SESSION_CONSTANTS, StringBoolean } from '../../constants';

type QueryValue = string | string[] | undefined;

export type CompareSelectionCard = {
  cardname?: string;
  checked?: boolean;
};

type BuildCompareUrlArgs = {
  baseUrl?: string;
  cardNames: string[];
  routerQuery?: Record<string, QueryValue>;
  targetExperience?: string;
};

type BuildSelectedCardsCompareUrlArgs = Omit<BuildCompareUrlArgs, 'cardNames'> & {
  selectedCards: CompareSelectionCard[];
};

type PersistCompareSelectionsArgs = {
  cobrandData: unknown;
  currentPath?: string;
};

type RestoreCompareSelectionsArgs<T> = {
  setCobrandData: (data: T) => void;
};

type CompareClickTrackArgs = {
  linkType: 'internal' | 'external';
  trackingProperties?: {
    trackingContentPosition?: string;
    trackingDescription?: string;
    additionalTrackingVariables?: string;
  };
};

export const toBoolean = (value?: StringBoolean | string | boolean): boolean =>
  value === true || value === 'true' || value === StringBoolean.TRUE;

export const getMaxCompareCards = (isDesktop: boolean): number =>
  isDesktop ? CARD_COMPARISON_LIMITS.MAX_CARDS_DESKTOP : CARD_COMPARISON_LIMITS.MAX_CARDS_MOBILE;

const normalizeQueryValue = (value: QueryValue): string | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
};

export const buildCompareUrlFromCardNames = ({
  baseUrl = '',
  cardNames,
  routerQuery,
  targetExperience,
}: BuildCompareUrlArgs): string => {
  const params = new URLSearchParams();

  cardNames.filter(Boolean).forEach((cardName, index) => {
    params.set(`cardtype${index + 1}`, cardName);
  });

  if (targetExperience) {
    params.set('experience', targetExperience);
  }

  if (routerQuery) {
    Object.entries(routerQuery).forEach(([key, value]) => {
      if (key === 'cardName') return;

      const normalizedValue = normalizeQueryValue(value);
      if (normalizedValue) {
        params.set(key, normalizedValue);
      }
    });
  }

  const queryString = params.toString();

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const buildSelectedCardsCompareUrl = ({ selectedCards, ...rest }: BuildSelectedCardsCompareUrlArgs): string =>
  buildCompareUrlFromCardNames({
    ...rest,
    cardNames: selectedCards
      .filter(card => card?.checked !== false)
      .map(card => card.cardname ?? '')
      .filter(Boolean),
  });

export const persistCompareSelections = ({ cobrandData, currentPath = '' }: PersistCompareSelectionsArgs): void => {
  if (typeof window === 'undefined') return;

  sessionStorage?.setItem(SESSION_CONSTANTS.COBRAND_DATA, JSON.stringify(cobrandData));
  sessionStorage?.setItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT, JSON.stringify(currentPath));
};

export const restoreCompareSelections = <T>({ setCobrandData }: RestoreCompareSelectionsArgs<T>): void => {
  if (typeof window === 'undefined') return;

  const cobrandDataFromSessionStorage = sessionStorage?.getItem(SESSION_CONSTANTS.COBRAND_DATA);

  if (cobrandDataFromSessionStorage) {
    try {
      setCobrandData(JSON.parse(cobrandDataFromSessionStorage) as T);
    } catch {
      // Ignore invalid persisted selections.
    } finally {
      sessionStorage.removeItem(SESSION_CONSTANTS.COBRAND_DATA);
    }
  }

  sessionStorage?.removeItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT);
};

const normalizeSelectedCardIds = (cards: CompareSelectionCard[] = []): string[] =>
  cards.map(card => card?.cardname ?? '');

export const hasSelectedCardsChanged = (
  previousSelectedCards: CompareSelectionCard[] = [],
  nextSelectedCards: CompareSelectionCard[] = []
): boolean => {
  if (previousSelectedCards.length !== nextSelectedCards.length) {
    return true;
  }

  const previousIds = normalizeSelectedCardIds(previousSelectedCards);
  const nextIds = normalizeSelectedCardIds(nextSelectedCards);

  return previousIds.some((cardId, index) => cardId !== nextIds[index]);
};

export const buildCompareClickTrackValue = ({ linkType, trackingProperties }: CompareClickTrackArgs): string => {
  const position = trackingProperties?.trackingContentPosition ?? '';
  const description = trackingProperties?.trackingDescription ?? '';
  const additional = trackingProperties?.additionalTrackingVariables ?? '';

  return `${position}|${description}|${linkType}|${additional}`;
};
